import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Badge,
  EmptyState,
  Modal,
  Input,
  Field,
  Skeleton,
  useToast,
} from '../ui';
import { TemplateSourceBadge } from '../components/templates/TemplateSourceBadge';
import { RowMenu } from '../components/templates/RowMenu';
import {
  templatesApi,
  type SpectoraImportResult,
  type TemplateSummary,
} from '../api/templates';

type ConfirmAction =
  | { kind: 'delete'; template: TemplateSummary }
  | { kind: 'create-blank' }
  | { kind: 'duplicate'; template: TemplateSummary }
  | null;

type ImportNotice =
  | { state: 'idle' }
  | { state: 'uploading'; filename: string }
  | { state: 'success'; result: SpectoraImportResult }
  | { state: 'error'; message: string };

export default function TemplatesIndexPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [rows, setRows] = useState<TemplateSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmAction>(null);
  const [busy, setBusy] = useState(false);
  const [blankName, setBlankName] = useState('New template');
  const [duplicateName, setDuplicateName] = useState('');
  const [importNotice, setImportNotice] = useState<ImportNotice>({ state: 'idle' });
  const fileRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    try {
      const data = await templatesApi.list();
      setRows(data);
    } catch (e) {
      setError((e as Error).message ?? 'Failed to load templates');
    }
  };

  useEffect(() => { load(); }, []);

  const runCreateBlank = async () => {
    setBusy(true);
    try {
      const tpl = await templatesApi.create(blankName.trim() || 'New template');
      setConfirm(null);
      toast.show({ tone: 'success', title: `Created "${tpl.name}"` });
      navigate(`/templates/${tpl.id}`);
    } catch (e) {
      toast.show({ tone: 'critical', title: (e as Error).message ?? 'Failed to create template' });
    } finally { setBusy(false); }
  };

  const runDuplicate = async (template: TemplateSummary) => {
    setBusy(true);
    try {
      const tpl = await templatesApi.duplicate(template.id, duplicateName.trim() || undefined);
      setConfirm(null);
      toast.show({ tone: 'success', title: `Duplicated as "${tpl.name}"` });
      await load();
      navigate(`/templates/${tpl.id}`);
    } catch (e) {
      toast.show({ tone: 'critical', title: (e as Error).message ?? 'Failed to duplicate template' });
    } finally { setBusy(false); }
  };

  const runDelete = async (template: TemplateSummary) => {
    setBusy(true);
    try {
      await templatesApi.remove(template.id);
      setConfirm(null);
      toast.show({ tone: 'success', title: `Deleted "${template.name}"` });
      await load();
    } catch (e) {
      toast.show({ tone: 'critical', title: (e as Error).message ?? 'Failed to delete template' });
    } finally { setBusy(false); }
  };

  const handleSetDefault = async (template: TemplateSummary) => {
    try {
      await templatesApi.setDefault(template.id);
      toast.show({ tone: 'success', title: `"${template.name}" is now your default` });
      await load();
    } catch (e) {
      toast.show({ tone: 'critical', title: (e as Error).message ?? 'Failed to set default' });
    }
  };

  const handleImportClick = () => fileRef.current?.click();

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setImportNotice({ state: 'uploading', filename: file.name });
    try {
      const result = await templatesApi.importSpectora(file);
      setImportNotice({ state: 'success', result });
      await load();
    } catch (err) {
      const message =
        (err as { response?: { data?: { error?: string } } }).response?.data?.error ??
        (err as Error).message ??
        'Import failed';
      setImportNotice({ state: 'error', message });
    }
  };

  return (
    <div className="px-4 md:px-6 py-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-display-sm text-ink leading-tight">Templates</h1>
          <div className="mt-1 text-body-sm text-ink-muted">
            The inspection scaffolds you snapshot into each new report.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".xls,.xlsx"
            hidden
            onChange={handleImportFile}
          />
          <Button variant="secondary" size="md" onClick={handleImportClick}>
            Import from Spectora
          </Button>
          <Button variant="primary" size="md" onClick={() => { setBlankName('New template'); setConfirm({ kind: 'create-blank' }); }}>
            New template
          </Button>
        </div>
      </div>

      {importNotice.state === 'uploading' && (
        <Card variant="outline" density="compact" className="mb-4">
          Uploading <strong>{importNotice.filename}</strong>… this usually takes a few seconds.
        </Card>
      )}

      {importNotice.state === 'success' && (
        <Card variant="elevated" density="standard" className="mb-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-medium text-ink mb-1">
                Imported <strong>{importNotice.result.template.name}</strong>
              </div>
              <div className="text-body-sm text-ink-muted">
                {importNotice.result.counts.sections} sections ·{' '}
                {importNotice.result.counts.components} components ·{' '}
                {importNotice.result.counts.deficienciesInserted} deficiency entries
                {importNotice.result.counts.deficienciesSkipped > 0 && (
                  <>
                    {' '}({importNotice.result.counts.deficienciesSkipped} already
                    in your library, skipped)
                  </>
                )}
                .
                {(importNotice.result.counts.infoFacts +
                  importNotice.result.counts.infoNotes +
                  importNotice.result.counts.limitations) > 0 && (
                  <>
                    {' '}
                    Captured {importNotice.result.counts.infoFacts} facts,{' '}
                    {importNotice.result.counts.infoNotes} notes, and{' '}
                    {importNotice.result.counts.limitations} limitations.
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(`/templates/${importNotice.result.template.id}`)}
              >
                Open
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setImportNotice({ state: 'idle' })}
              >
                Dismiss
              </Button>
            </div>
          </div>
        </Card>
      )}

      {importNotice.state === 'error' && (
        <Card variant="outline" density="compact" className="mb-4 border-red-200 text-red-700">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-medium">Import failed</div>
              <div className="text-body-sm">{importNotice.message}</div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setImportNotice({ state: 'idle' })}>
              Dismiss
            </Button>
          </div>
        </Card>
      )}

      {error && !rows && (
        <EmptyState
          title="Couldn't load templates"
          description={error}
          action={<Button variant="secondary" onClick={load}>Retry</Button>}
        />
      )}

      {!rows && !error && (
        <div className="space-y-3">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="card p-4 flex items-center gap-4">
              <div className="flex-1 min-w-0 space-y-2">
                <div className="flex items-center gap-2">
                  <Skeleton height="h-4" width="w-44" />
                  <Skeleton height="h-5" width="w-16" shape="pill" />
                </div>
                <Skeleton height="h-3" width="w-64" />
              </div>
              <Skeleton height="h-6" width="w-6" shape="circle" />
            </div>
          ))}
        </div>
      )}

      {rows && rows.length === 0 && (
        <EmptyState
          title="No templates yet"
          description="Your first template will appear here. Create a new one or import from Spectora."
        />
      )}

      {rows && rows.length > 0 && (
        <Card unpadded variant="default">
          <ul className="divide-y divide-border-subtle">
            {rows.map((t) => (
              <li key={t.id}>
                <TemplateRow
                  template={t}
                  onOpen={() => navigate(`/templates/${t.id}`)}
                  onSetDefault={() => handleSetDefault(t)}
                  onDuplicate={() => { setDuplicateName(`${t.name} (copy)`); setConfirm({ kind: 'duplicate', template: t }); }}
                  onDelete={() => setConfirm({ kind: 'delete', template: t })}
                  canDelete={rows.length > 1}
                />
              </li>
            ))}
          </ul>
        </Card>
      )}

      {confirm?.kind === 'create-blank' && (
        <Modal open title="New template" onClose={() => !busy && setConfirm(null)} size="sm">
          <div className="space-y-3">
            <Field label="Template name">
              <Input
                autoFocus
                value={blankName}
                onChange={(e) => setBlankName(e.target.value)}
                placeholder="e.g. Residential (default)"
              />
            </Field>
            <div className="text-body-sm text-ink-muted">
              Starts from the Flowspect default sections. Edit as needed.
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
              <Button variant="primary" onClick={runCreateBlank} disabled={busy || !blankName.trim()}>
                {busy ? 'Creating…' : 'Create template'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {confirm?.kind === 'duplicate' && (
        <Modal open title={`Duplicate "${confirm.template.name}"`} onClose={() => !busy && setConfirm(null)} size="sm">
          <div className="space-y-3">
            <Field label="New template name">
              <Input
                autoFocus
                value={duplicateName}
                onChange={(e) => setDuplicateName(e.target.value)}
              />
            </Field>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
              <Button
                variant="primary"
                onClick={() => confirm.kind === 'duplicate' && runDuplicate(confirm.template)}
                disabled={busy}
              >
                {busy ? 'Duplicating…' : 'Duplicate'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {confirm?.kind === 'delete' && (
        <Modal open title={`Delete "${confirm.template.name}"?`} onClose={() => !busy && setConfirm(null)} size="sm">
          <div className="space-y-3">
            <p className="text-body-sm text-ink-muted">
              This removes the template from your library. Inspections that already use it are unaffected — they keep their own copy.
              {confirm.template.isDefault && (
                <>
                  {' '}
                  <strong>Your default will switch</strong> to the oldest remaining template.
                </>
              )}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={() => confirm.kind === 'delete' && runDelete(confirm.template)}
                disabled={busy}
              >
                {busy ? 'Deleting…' : 'Delete template'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

interface TemplateRowProps {
  template: TemplateSummary;
  onOpen: () => void;
  onSetDefault: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  canDelete: boolean;
}


function TemplateRow({
  template, onOpen, onSetDefault, onDuplicate, onDelete, canDelete,
}: TemplateRowProps) {
  return (
    <div
      className="flex items-center gap-4 px-5 py-4 hover:bg-surface-2 cursor-pointer"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpen(); }}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-ink">{template.name}</span>
          {template.isDefault && <Badge tone="success">Default</Badge>}
          <TemplateSourceBadge source={template.source} />
        </div>
        <div className="text-body-sm text-ink-muted mt-1">
          {template.sectionCount} sections · {template.componentCount} components ·
          {' '}Updated {new Date(template.updatedAt).toLocaleDateString()}
        </div>
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        <RowMenu
          items={[
            { label: 'Open', icon: 'chev', onClick: onOpen },
            { label: 'Set as default', icon: 'check', onClick: onSetDefault, disabled: template.isDefault },
            { label: 'Duplicate', icon: 'pencil', onClick: onDuplicate },
            'separator',
            {
              label: canDelete ? 'Delete' : 'Delete (last template)',
              icon: 'trash',
              danger: true,
              onClick: onDelete,
              disabled: !canDelete,
            },
          ]}
        />
      </div>
    </div>
  );
}
